import React from 'react';
import { Button } from '@popmenu/admin-ui';
import { Typography, Link, makeStyles } from '@popmenu/common-ui';
import { useIntl } from 'react-intl';
import { useCurrentSession } from '~/shared/CurrentSessionProvider';
import Loading from '~/shared/Loading';
import Grid from '~/shared/Grid';
import { useAppointmentLink } from '~/admin/hooks/useAppointmentLink';
import SelfSignUpStepContainer from './SelfSignUpStepContainer';

export const REDIRECT_INTERVAL_MS = 5000;

const useStyles = makeStyles(({ spacing }) => ({
  actionButtons: {
    marginTop: spacing(3.5),
  },
  container: {
    minHeight: 'calc(100vh - 200px)',
    padding: spacing(5, 2),
  },
  content: {
    maxWidth: '39rem',
  },
  description: {
    marginTop: spacing(2),
  },
  footerText: {
    marginTop: spacing(2),
  },
  title: {
    marginBottom: spacing(4),
  },
}));

const useMessages = () => {
  const intl = useIntl();

  return {
    appointmentDescription: intl.formatMessage({ defaultMessage: 'Your appointment link will open automatically. If it doesn’t, use the button below to keep things moving.' }),
    appointmentMissingBody: intl.formatMessage({ defaultMessage: "We couldn't find a scheduling link for your account. Please contact support@popmenu.com." }),
    appointmentMissingTitle: intl.formatMessage({ defaultMessage: 'Scheduling link unavailable' }),
    dashboardLink: intl.formatMessage({ defaultMessage: 'MyPop dashboard' }),
    footerPrefix: intl.formatMessage({ defaultMessage: 'Or, go to your ' }),
    scheduleMeeting: intl.formatMessage({ defaultMessage: 'Schedule a meeting with Sales' }),
    successTitle: intl.formatMessage({ defaultMessage: 'Hang tight - we’re opening your scheduling page.' }),
  };
};

const AppointmentLinkPage = () => {
  const classes = useStyles();
  const messages = useMessages();
  const session = useCurrentSession<'admin'>();
  const [hasTriedOpeningLink, setHasTriedOpeningLink] = React.useState(false);
  const signInWithRedirectUrl = `/?redirect=${encodeURIComponent('/sign-up/appointment-link')}`;

  const { appointmentLink, query: { loading } } = useAppointmentLink({ skip: !session.user });

  React.useEffect(() => {
    if (session.user) return;

    window.location.assign(signInWithRedirectUrl);
  }, [session.user, signInWithRedirectUrl]);

  React.useEffect(() => {
    let timeoutId: number | undefined;

    if (appointmentLink && !hasTriedOpeningLink) {
      timeoutId = window.setTimeout(() => {
        window.open(appointmentLink, '_blank', 'noopener,noreferrer');
        setHasTriedOpeningLink(true);
      }, REDIRECT_INTERVAL_MS);
    }

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [appointmentLink, hasTriedOpeningLink]);

  if (!session.user) {
    return null;
  }

  if (loading) {
    return (
      <Loading />
    );
  }

  return (
    <SelfSignUpStepContainer>
      <Grid container direction="column" justifyContent="center" alignItems="center" className={classes.container}>
        <Grid item xs={12} className={classes.content}>
          <Typography align="center" variant="h5" className={classes.title}>
            {appointmentLink ? messages.successTitle : messages.appointmentMissingTitle}
          </Typography>
          <Typography align="center" variant="body1" className={classes.description}>
            {appointmentLink ? messages.appointmentDescription : messages.appointmentMissingBody}
          </Typography>

          <Grid container direction="column" alignItems="center" className={classes.actionButtons} spacing={2}>
            <Grid item>
              {appointmentLink ? (
                <Button
                  component="a"
                  data-testid="appointment-link-schedule-sales"
                  href={appointmentLink}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {messages.scheduleMeeting}
                </Button>
              ) : (
                <Button
                  data-testid="appointment-link-schedule-sales"
                  disabled
                >
                  {messages.scheduleMeeting}
                </Button>
              )}
            </Grid>
            <Grid item>
              <Typography align="center" variant="body2" className={classes.footerText}>
                {messages.footerPrefix}
                <Link href="/" data-testid="appointment-link-go-dashboard" style={{ display: 'inline' }}>
                  {messages.dashboardLink}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </SelfSignUpStepContainer>
  );
};

export default AppointmentLinkPage;
